import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './ApplicationCategoryCard.styles.scss';
import { useNavigate } from 'react-router-dom';
import { getFieldConfig } from '../../../../constants/serviceFieldConfig';
import { APPLICATION_TITLE_MAX_CHAR } from '../../../../constants/appConfig';
import { showToastr } from '../../../ToastrMessage/Toast.helper';
import { APP_LABEL } from '../../../../translations';
import OADialog from '../../OADialog/OADialog.component';

const getStatus = (startDate, endDate) => {
  const now = new Date();
  if (endDate && now > new Date(endDate)) return { label: 'Closed', cls: 'closed' };
  if (startDate && now < new Date(startDate)) return { label: 'Upcoming', cls: 'upcoming' };
  return { label: 'Active', cls: 'active' };
};

const ApplicationCategoryCard = (props) => {
  const {
    applicationName,
    applicationStartDate,
    applicationImageURL,
    applicationDescription,
    applicationLastDate,
    applicationsCategoryName,
    serviceState,
  } = props;

  const navigate = useNavigate();
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [pathToShare, setPathToShare] = useState('');
  const domain = window.location.origin;
  const urlCategory = serviceState || applicationsCategoryName;
  const cscCentersPath = `${domain}/services/${urlCategory}/${applicationName}`;
  const status = getStatus(applicationStartDate, applicationLastDate);

  const handleCardClick = () => {
    navigate(`/services/${urlCategory}/${applicationName}`);
  };

  const trimDownApplicationName = (name) => {
    if (!name) return 'application';
    if (name.length > APPLICATION_TITLE_MAX_CHAR) return name.substring(0, APPLICATION_TITLE_MAX_CHAR) + '...';
    return name;
  };

  const applicationCategoryCopyIconHandle = (e) => {
    e?.stopPropagation();
    navigator.clipboard.writeText(cscCentersPath);
    showToastr('success', `${APP_LABEL['en'].copyLinkPrefix} ${applicationName} ${APP_LABEL['en'].copyLinkSuffix}`);
  };

  const applicationCategoryShareIconHandle = (e) => {
    e?.stopPropagation();
    setPathToShare(cscCentersPath);
    setOpenShareDialog(true);
  };

  const handleActionKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
  };

  return (
    <article
      className="app-category-card"
      onClick={handleCardClick}
      onKeyDown={(e) => { if (e.key === 'Enter') handleCardClick(); }}
      role="button"
      tabIndex={0}
      aria-label={`View ${applicationName || 'service'}`}
    >
      {/* Media: hero image + overlay actions + category chip */}
      <div className="app-category-card__media">
        <img
          className="app-category-card__image"
          src={applicationImageURL}
          alt={applicationName || 'application'}
          loading="lazy"
        />
        <div className="app-category-card__overlay-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton
            aria-label="copy link"
            className="app-category-card__overlay-btn"
            size="small"
            onClick={applicationCategoryCopyIconHandle}
            onKeyDown={handleActionKeyDown}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="share"
            className="app-category-card__overlay-btn"
            size="small"
            onClick={applicationCategoryShareIconHandle}
            onKeyDown={handleActionKeyDown}
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </div>
        {applicationsCategoryName && (
          <span className="app-category-card__category">{applicationsCategoryName}</span>
        )}
      </div>

      {/* Body: status + title + description */}
      <div className="app-category-card__body">
        <span className={`app-category-card__status app-category-card__status--${status.cls}`}>
          {status.label}
        </span>
        <h3 className="app-category-card__title" title={applicationName}>
          {trimDownApplicationName(applicationName)}
        </h3>
        <p className="app-category-card__description">
          {applicationDescription}
        </p>
      </div>

      {/* Date pills */}
      {getFieldConfig(applicationsCategoryName).dates && (
      <div className="app-category-card__dates">
        <div className="app-category-card__date-pill app-category-card__date-pill--start">
          <CalendarMonthIcon className="app-category-card__date-icon" fontSize="small" />
          <div className="app-category-card__date-text">
            <span className="app-category-card__date-label">Starts</span>
            <span className="app-category-card__date-value">{applicationStartDate ?? '01 Jan 2024'}</span>
          </div>
        </div>
        <div className="app-category-card__date-pill app-category-card__date-pill--end">
          <EventBusyOutlinedIcon className="app-category-card__date-icon" fontSize="small" />
          <div className="app-category-card__date-text">
            <span className="app-category-card__date-label">Closes</span>
            <span className="app-category-card__date-value">{applicationLastDate ?? 'Ongoing'}</span>
          </div>
        </div>
      </div>
      )}

      {/* CTA */}
      <div className="app-category-card__cta" role="presentation">
        <span className="app-category-card__cta-label">View Details</span>
        <ArrowForwardIcon className="app-category-card__cta-arrow" fontSize="small" />
      </div>

      <OADialog
        setOpenShareDialog={setOpenShareDialog}
        openShareDialog={openShareDialog}
        pathToShare={pathToShare}
        confirmationMessage={APP_LABEL['en'].cancelApplication}
        rightButtonTitle={APP_LABEL['en'].cancelBtnTitle}
      />
    </article>
  );
};

export default ApplicationCategoryCard;
